import { useEffect, useRef, useState } from 'react'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { CircularProgress, useMediaQuery } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { verifyOtp, requestOtp } from '@/shared/api/otp'
import { normalizeError } from '@/lib/apiErrors'
import { decodeJwtPayload } from '@/lib/jwtDecode'
import { useAppDispatch } from '@/store'
import { setCredentials } from '@/store/authSlice'
import { colors, breakpoints } from '@/styles/designTokens'
import { getVerifyOtpErrorMessageKey } from '../../lib/verifyOtpErrorCodeMapper'
import { getErrorMessageKey } from '../../lib/errorCodeMapper'
import {
	StyledContentBox,
	StyledVerifyTitle,
	StyledVerifyText,
	StyledOtpInputContainer,
	StyledOtpInput,
	StyledErrorBox,
	StyledErrorText,
	StyledButton,
	StyledFooterBox,
	StyledButtonContainer,
	StyledHelperLinksBox,
	StyledHelperText,
	StyledResultIcon,
	StyledResultTitle,
	StyledResultMessage,
} from '../../SignInModal.styled'
import { StyledVerifyContentWrapper, StyledLoadingContent } from './VerifyOtpStep.styled'
import type { StepComponentProps } from '../../SignInModal.types'

type VerifyState = 'input' | 'success' | 'failure'

const RESEND_COUNTDOWN_SECONDS = 30

// How long the success screen is shown before redirecting/closing the modal.
const SUCCESS_REDIRECT_DELAY_MS = 1500

export function VerifyOtpStep({ state, setState, locationId, onLoginSuccess }: StepComponentProps) {
	const dispatch = useAppDispatch()
	const { t } = useFormatMessage()
	const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile}px)`)
	const [verifyState, setVerifyState] = useState<VerifyState>('input')
	const [resendCountdown, setResendCountdown] = useState(RESEND_COUNTDOWN_SECONDS)
	const isVerifyingRef = useRef(false)
	const isResendingRef = useRef(false)
	const otpDigits = state.otpInput.split('').concat(Array(6 - state.otpInput.length).fill(''))

	// Countdown timer for resend code (Figma: "Try again in 30 seconds")
	useEffect(() => {
		if (resendCountdown <= 0) return
		const timer = setInterval(() => {
			setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1))
		}, 1000)
		return () => clearInterval(timer)
	}, [resendCountdown])

	// Auto-close on success
	useEffect(() => {
		if (verifyState !== 'success') return
		const timer = setTimeout(() => {
			onLoginSuccess?.()
		}, SUCCESS_REDIRECT_DELAY_MS)
		return () => clearTimeout(timer)
	}, [verifyState, onLoginSuccess])

	const handleOtpChange = (index: number, value: string) => {
		// Only allow digits
		if (!/^\d*$/.test(value)) return

		let newOtp = ''

		// Handle paste: if value has multiple digits, spread them across boxes
		if (value.length > 1) {
			newOtp = value.slice(0, 6)
		} else {
			// Handle single digit: replace at current index
			newOtp = otpDigits.map((digit, i) => (i === index ? value : digit)).join('')
		}

		setState((prev) => ({
			...prev,
			otpInput: newOtp.slice(0, 6),
			errorMessage: null,
		}))

		// Move to next input if single digit entered
		if (value.length === 1 && value && index < 5) {
			const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement
			nextInput?.focus()
		}

		// Move focus to last filled digit if pasted multiple digits
		if (value.length > 1) {
			const lastFilledIndex = Math.min(newOtp.length - 1, 5)
			if (lastFilledIndex >= 0 && lastFilledIndex < 6) {
				const focusInput = document.getElementById(
					`otp-${lastFilledIndex}`,
				) as HTMLInputElement
				focusInput?.focus()
			}
		}

		// Auto-submit when all 6 digits are filled
		if (newOtp.length === 6) {
			void handleVerifyOtp(newOtp)
		}
	}

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' && !state.otpInput[index] && index > 0) {
			const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement
			prevInput?.focus()
		}
	}

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault()
		const pastedData = e.clipboardData.getData('text')

		// Only allow digits
		if (!/^\d+$/.test(pastedData)) return

		// Take only first 6 digits
		const digits = pastedData.slice(0, 6)

		setState((prev) => ({
			...prev,
			otpInput: digits,
			errorMessage: null,
		}))

		// Focus on last filled digit
		const lastIndex = Math.min(digits.length - 1, 5)
		if (lastIndex >= 0) {
			setTimeout(() => {
				const focusInput = document.getElementById(`otp-${lastIndex}`) as HTMLInputElement
				focusInput?.focus()
			}, 0)
		}

		// Auto-submit if all 6 digits
		if (digits.length === 6) {
			setTimeout(() => {
				void handleVerifyOtp(digits)
			}, 0)
		}
	}

	const handleVerifyOtp = async (code: string) => {
		if (!code || code.length !== 6) {
			return
		}

		if (!state.correlationId || !state.channel || !state.identifier) {
			return
		}

		if (isVerifyingRef.current) {
			return
		}
		isVerifyingRef.current = true

		setState((prev) => ({
			...prev,
			isRequestInProgress: true,
			errorMessage: null,
		}))

		try {
			const response = await verifyOtp({
				channel: state.channel,
				identifier: state.identifier,
				otp: code,
				correlationId: state.correlationId,
			})

			// Decode and validate JWT payload
			const payload = decodeJwtPayload(response.accessToken)

			// Save to Redux (redux-persist automatically syncs to localStorage)
			dispatch(
				setCredentials({
					token: response.accessToken,
					user: {
						id: payload.user?.guid || payload.guid || '',
						email: payload.user?.email || '',
						phone: payload.user?.phone || '',
						name: payload.user?.name || '',
					},
				}),
			)

			// Show the success screen; the success useEffect handles the
			// delayed onLoginSuccess() (redirect + close) and cleans up on unmount.
			isVerifyingRef.current = false
			setVerifyState('success')
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getVerifyOtpErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
			})
			isVerifyingRef.current = false
			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage: t(errorKey),
			}))
			setVerifyState('failure')
		}
	}

	const handleResendCode = async () => {
		if (!state.channel || !state.identifier) return
		if (isResendingRef.current) return
		isResendingRef.current = true

		setState((prev) => ({
			...prev,
			otpInput: '',
			errorMessage: null,
			isRequestInProgress: true,
		}))

		try {
			const response = await requestOtp({
				channel: state.channel,
				identifier: state.identifier,
				locationId,
			})

			setState((prev) => ({
				...prev,
				correlationId: response.correlationId,
				isRequestInProgress: false,
			}))
			setResendCountdown(RESEND_COUNTDOWN_SECONDS)
			isResendingRef.current = false
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
			})
			isResendingRef.current = false
			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage: t(errorKey),
			}))
			// Do not restart the countdown — leave the resend link active so the user can retry immediately
		}
	}

	const handleChangeIdentifier = () => {
		setState((prev) => ({
			...prev,
			isVerifyStepShown: false,
			otpInput: '',
			errorMessage: null,
			correlationId: null,
		}))
	}

	const handleTryAgain = () => {
		isVerifyingRef.current = false
		setState((prev) => ({
			...prev,
			otpInput: '',
			errorMessage: null,
		}))
		setVerifyState('input')
	}

	const getDisplayIdentifier = (): string => {
		if (!state.identifier) return ''
		if (state.channel === 'SMS') {
			// Format E.164 phone for display, e.g. +11234567890 -> +1 123 456 7890
			const digits = state.identifier.replace(/[^\d+]/g, '')
			const match = digits.match(/^(\+\d)(\d{3})(\d{3})(\d{4})$/)
			if (match) {
				return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`
			}
			return state.identifier
		}
		return state.identifier
	}

	const displayIdentifier = getDisplayIdentifier()
	const isSms = state.channel === 'SMS'

	// Success State
	if (verifyState === 'success') {
		return (
			<StyledContentBox>
				<StyledResultIcon isSuccess>
					<CheckCircleIcon sx={{ fontSize: '64px', color: colors.success }} />
				</StyledResultIcon>

				<StyledResultTitle>{t('signin:verifyOtpStep:welcomeTitle')}</StyledResultTitle>

				<StyledResultMessage>
					{t('signin:verifyOtpStep:welcomeSubtitle')}
				</StyledResultMessage>
			</StyledContentBox>
		)
	}

	// Failure State (Per Figma Design)
	if (verifyState === 'failure') {
		return (
			<>
				<StyledContentBox>
					<StyledResultIcon isSuccess={false}>
						<ErrorIcon sx={{ fontSize: '64px', color: colors.error }} />
					</StyledResultIcon>

					<StyledResultTitle>{t('signin:verifyOtpStep:failureTitle')}</StyledResultTitle>

					<StyledResultMessage>
						{state.errorMessage ?? t('signin:verifyOtpStep:failureMessage')}
					</StyledResultMessage>
				</StyledContentBox>

				<StyledFooterBox>
					<StyledButtonContainer>
						<StyledButton type="button" onClick={handleTryAgain}>
							{t('signin:verifyOtpStep:tryAgainButton')}
						</StyledButton>
					</StyledButtonContainer>
				</StyledFooterBox>
			</>
		)
	}

	// Input State (default)
	return (
		<>
			<StyledContentBox>
				<StyledVerifyContentWrapper>
					<StyledVerifyText>
						{t('signin:verifyOtpStep:instructionPrefix')}
					</StyledVerifyText>
					<StyledVerifyTitle>{displayIdentifier}</StyledVerifyTitle>

					<StyledOtpInputContainer>
						{Array.from({ length: 6 }).map((_, index) => (
							<StyledOtpInput
								key={index}
								id={`otp-${index}`}
								type="text"
								inputMode="numeric"
								maxLength={1}
								value={otpDigits[index] || ''}
								onChange={(e) => handleOtpChange(index, e.target.value)}
								onKeyDown={(e) => handleKeyDown(index, e)}
								onPaste={handlePaste}
								disabled={state.isRequestInProgress}
								aria-invalid={!!state.errorMessage}
								aria-label={t('signin:verifyOtpStep:digitAriaLabel', {
									position: index + 1,
								})}
							/>
						))}
					</StyledOtpInputContainer>

					{state.errorMessage && (
						<StyledErrorBox role="alert">
							<StyledErrorText>{state.errorMessage}</StyledErrorText>
						</StyledErrorBox>
					)}
				</StyledVerifyContentWrapper>

				<StyledHelperLinksBox>
					{isMobile ? (
						<StyledHelperText>
							{resendCountdown > 0 ? (
								t('signin:verifyOtpStep:resendCountdown', {
									seconds: resendCountdown,
								})
							) : (
								<a
									href="#resend"
									onClick={(e) => {
										e.preventDefault()
										void handleResendCode()
									}}
								>
									{t('signin:verifyOtpStep:resendButton')}
								</a>
							)}
						</StyledHelperText>
					) : (
						<StyledHelperText>
							{resendCountdown > 0 ? (
								t('signin:verifyOtpStep:resendCountdown', {
									seconds: resendCountdown,
								})
							) : (
								<>
									{t('signin:verifyOtpStep:resendPrompt')}{' '}
									<a
										href="#resend"
										onClick={(e) => {
											e.preventDefault()
											void handleResendCode()
										}}
									>
										{t('signin:verifyOtpStep:resendButton')}
									</a>
								</>
							)}
						</StyledHelperText>
					)}
					<StyledHelperText>
						<a
							href="#change-identifier"
							onClick={(e) => {
								e.preventDefault()
								handleChangeIdentifier()
							}}
						>
							{t(
								isSms
									? 'signin:verifyOtpStep:changePhoneButton'
									: 'signin:verifyOtpStep:changeEmailButton',
							)}
						</a>
					</StyledHelperText>
				</StyledHelperLinksBox>
			</StyledContentBox>

			<StyledFooterBox>
				<StyledButtonContainer>
					<StyledButton
						type="button"
						onClick={() => void handleVerifyOtp(state.otpInput)}
						disabled={state.otpInput.length !== 6 || state.isRequestInProgress}
					>
						{state.isRequestInProgress ? (
							<StyledLoadingContent>
								<CircularProgress size={16} sx={{ color: 'inherit' }} />
								{t('signin:verifyOtpStep:verifyingButton')}
							</StyledLoadingContent>
						) : (
							t('signin:verifyOtpStep:verifyButton')
						)}
					</StyledButton>
				</StyledButtonContainer>
			</StyledFooterBox>
		</>
	)
}
